import { useState } from 'react';
import { WorkoutService } from '../../../../lib/workoutService';
import { displayNameFromProfile } from '../../../../pages/account/accountShared';
import { toLocalDateKey } from '../../../../utils/dateKey';
import type { AssignMembersCache } from './useAssignMembersCache';

export type AssignPlanSuccessToast = {
  planName: string;
  memberDisplayName: string;
};

export type UseAssignWorkoutPlanModalParams = {
  profileId: string | undefined;
  workoutPlans: Array<{ id: string; name?: string }>;
  loadLibraryContent: (options?: { silent?: boolean }) => Promise<void>;
  setOpenWorkoutPlanMenu: (id: string | null) => void;
  onNavigateToAddMember: () => void;
  assignMembersCache: AssignMembersCache;
};

/** Assign workout plan modal — Library plans tab (P15-R4). */
export function useAssignWorkoutPlanModal({
  workoutPlans,
  loadLibraryContent,
  setOpenWorkoutPlanMenu,
  onNavigateToAddMember,
  assignMembersCache,
}: UseAssignWorkoutPlanModalParams) {
  const { assignMembers, loadingAssignMembers, loadAssignMembersIfNeeded } = assignMembersCache;

  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assigningPlanId, setAssigningPlanId] = useState<string | null>(null);
  const [selectedAssignMemberId, setSelectedAssignMemberId] = useState('');
  const [assignmentStartDate, setAssignmentStartDate] = useState<string>(() => toLocalDateKey(new Date()));
  const [assignmentNotes, setAssignmentNotes] = useState('');
  const [isAssigning, setIsAssigning] = useState(false);
  const [assignmentError, setAssignmentError] = useState<string | null>(null);
  const [assignPlanSuccessToast, setAssignPlanSuccessToast] = useState<AssignPlanSuccessToast | null>(null);

  const handleAssignWorkoutPlan = async (planId: string) => {
    setOpenWorkoutPlanMenu(null);
    setAssigningPlanId(planId);
    setSelectedAssignMemberId('');
    setAssignmentStartDate(toLocalDateKey(new Date()));
    setAssignmentNotes('');
    setAssignmentError(null);
    setShowAssignModal(true);

    const loadError = await loadAssignMembersIfNeeded();
    if (loadError) setAssignmentError(loadError);
  };

  const handleCloseAssignModal = () => {
    setShowAssignModal(false);
    setAssigningPlanId(null);
    setSelectedAssignMemberId('');
    setAssignmentStartDate(toLocalDateKey(new Date()));
    setAssignmentNotes('');
    setAssignmentError(null);
  };

  const handleAddMemberFromEmpty = () => {
    handleCloseAssignModal();
    onNavigateToAddMember();
  };

  const handleAssignPlanSubmit = async () => {
    if (!selectedAssignMemberId) {
      setAssignmentError('Please select a member');
      return;
    }
    if (!assignmentStartDate) {
      setAssignmentError('Please select a start date');
      return;
    }
    if (!assigningPlanId) {
      setAssignmentError('Workout plan ID is missing');
      return;
    }

    try {
      setIsAssigning(true);
      setAssignmentError(null);

      await WorkoutService.assignPlanToMember({
        userId: selectedAssignMemberId,
        workoutPlanId: assigningPlanId,
        startDate: assignmentStartDate,
        notes: assignmentNotes || undefined,
      });

      const plan = workoutPlans.find((p) => p.id === assigningPlanId);
      const member = assignMembers.find((m) => m.memberId === selectedAssignMemberId);
      const memberDisplayName =
        (member && (displayNameFromProfile(member.member) || member.member?.email)) || 'member';

      handleCloseAssignModal();
      await loadLibraryContent({ silent: true });
      setAssignPlanSuccessToast({
        planName: plan?.name || 'Workout plan',
        memberDisplayName,
      });
    } catch (err: unknown) {
      console.error('Error assigning workout plan:', err);
      setAssignmentError(
        err instanceof Error ? err.message : 'Failed to assign workout plan. Please try again.',
      );
    } finally {
      setIsAssigning(false);
    }
  };

  return {
    showAssignModal,
    assigningPlanId,
    assignMembers,
    loadingAssignMembers,
    selectedAssignMemberId,
    setSelectedAssignMemberId,
    assignmentStartDate,
    setAssignmentStartDate,
    assignmentNotes,
    setAssignmentNotes,
    assignmentError,
    isAssigning,
    assignPlanSuccessToast,
    setAssignPlanSuccessToast,
    handleAssignWorkoutPlan,
    handleCloseAssignModal,
    handleAssignPlanSubmit,
    handleAddMemberFromEmpty,
  };
}
